import { Controller } from '@hotwired/stimulus';
import { DirectUpload } from '@rails/activestorage';
import Dropzone from 'dropzone';
import { getMetaValue, findElement, removeElement, insertAfter } from './helpers';

Dropzone.autoDiscover = false;

export default class extends Controller {
  static targets = ['input', 'submit', 'container'];
  static values = {
    maxFiles: Number,
    maxFileSize: Number,
    uploadNotReadyMessage: String
  };

  connect() {
    this.dropZone = createDropZone(this);
    this.hideFileInput();
    this.bindEvents();
  }

  disconnect() {
    if (this.dropZone) {
      this.dropZone.destroy();
      this.dropZone = null;
    }
  }

  // Called from submit button: warn if a file is still uploading.
  guardSubmit(event) {
    if (!this.dropZone) return;

    const files = this.dropZone.getAcceptedFiles();
    if (files.length === 0) return;

    const pending = files.some((file) => file.status !== Dropzone.SUCCESS);
    if (!pending) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.alert(
      this.uploadNotReadyMessageValue || 'Please wait until the upload is finished.'
    );
  }

  // Private
  hideFileInput() {
    this.inputTarget.disabled = true;
    this.inputTarget.style.display = 'none';
  }

  bindEvents() {
    this.dropZone.on('addedfile', (file) => {
      setTimeout(() => {
        file.accepted && createDirectUploadController(this, file).start();
      }, 500);
    });

    this.dropZone.on('removedfile', (file) => {
      file.controller && removeElement(file.controller.hiddenInput);
    });

    this.dropZone.on('canceled', (file) => {
      file.controller && file.controller.xhr.abort();
    });

    this.dropZone.on('processing', () => {
      this.disableSubmit();
    });

    this.dropZone.on('queuecomplete', () => {
      this.enableSubmit();
    });

    this.dropZone.on('error', () => {
      this.enableSubmit();
    });
  }

  disableSubmit() {
    if (this.hasSubmitTarget) this.submitTarget.disabled = true;
  }

  enableSubmit() {
    if (this.hasSubmitTarget) this.submitTarget.disabled = false;
  }

  get headers() {
    return { 'X-CSRF-Token': getMetaValue('csrf-token') };
  }

  get url() {
    return this.inputTarget.getAttribute('data-direct-upload-url');
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.directUpload = createDirectUpload(file, source.url, this);
    this.source = source;
    this.file = file;
  }

  start() {
    this.file.controller = this;
    this.hiddenInput = this.createHiddenInput();
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(error);
      } else {
        this.hiddenInput.value = attributes.signed_id;
        this.emitDropzoneSuccess();
      }
    });
  }

  // Private
  createHiddenInput() {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = this.source.inputTarget.name;
    insertAfter(input, this.source.inputTarget);
    return input;
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr);
    this.emitDropzoneUploading();
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr;
    this.xhr.upload.addEventListener('progress', (event) =>
      this.uploadRequestDidProgress(event)
    );
  }

  uploadRequestDidProgress(event) {
    const progress = (event.loaded / event.total) * 100;
    findElement(this.file.previewTemplate, '.dz-upload').style.width = `${progress}%`;
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING;
    this.source.dropZone.emit('processing', this.file);
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.source.dropZone.emit('error', this.file, error);
    this.source.dropZone.emit('complete', this.file);
    this.source.dropZone.emit('queuecomplete');
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.source.dropZone.emit('success', this.file);
    this.source.dropZone.emit('complete', this.file);
    this.source.dropZone.emit('queuecomplete');
  }
}

// Top level...
function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file);
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller);
}

const createDropZone = (controller) => {
  return new Dropzone(controller.containerTarget, {
    url: controller.url,
    headers: controller.headers,
    maxFiles: controller.maxFilesValue || true,
    maxFilesize: controller.maxFileSizeValue || 20,
    autoQueue: false,
    addRemoveLinks: true,
    uploadMultiple: false
  });
};
